import React, { useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faArrowRight, faChartLine, faLightbulb,
    faIndustry, faTimes, faThLarge, faList
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import { adventUseCases, categoryColors, typeColors } from '../../data/adventData';
import { useClarity } from '../hooks/useClarity';

const UseCases = () => {
    const { trackEvent, trackBookingClick, setTag } = useClarity();

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSector, setSelectedSector] = useState('All');
    const [selectedType, setSelectedType] = useState('All');
    const [viewMode, setViewMode] = useState('list');

    // Determine the current "advent day" - which use cases are unlocked
    const today = new Date();
    const currentDay = today.getMonth() === 11 ? today.getDate() : 25; // December = month 11, else show all

    // Get all published use cases up to current day (advent calendar logic)
    // Sorted by day descending (newest first)
    const publishedUseCases = adventUseCases
        .filter(uc => uc.day <= currentDay)
        .sort((a, b) => b.day - a.day);

    // Featured = current day's entry (highest day number)
    const featuredUseCase = publishedUseCases[0];

    // Remaining use cases for the list
    const remainingUseCases = publishedUseCases.slice(1);

    // Get unique sectors and types from published use cases
    const sectors = ['All', ...new Set(publishedUseCases.map(uc => uc.category))];
    const types = ['All', ...Object.keys(typeColors)];

    // Track page view on mount
    useEffect(() => {

        trackEvent('usecases_page_viewed', {
            total_published: publishedUseCases.length,
            featured_usecase: featuredUseCase?.title,
            current_day: currentDay
        });
        setTag('page_type', 'use_cases_directory');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter remaining use cases based on search/filters
    const filteredUseCases = useMemo(() => {
        return remainingUseCases.filter(useCase => {
            const matchesSearch = searchTerm === '' ||
                useCase.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                useCase.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
                useCase.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                useCase.category.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesSector = selectedSector === 'All' || useCase.category === selectedSector;
            const matchesType = selectedType === 'All' || useCase.type === selectedType;

            return matchesSearch && matchesSector && matchesType;
        });
    }, [remainingUseCases, searchTerm, selectedSector, selectedType]);

    const getCategoryStyle = (category) => {
        return categoryColors[category] || { bg: "rgba(108, 117, 125, 0.1)", border: "#6c757d", text: "#6c757d" };
    };

    const getTypeStyle = (type) => {
        return typeColors[type] || { bg: "rgba(108, 117, 125, 0.1)", border: "#6c757d", text: "#6c757d" };
    };

    const clearFilters = () => {
        trackEvent('usecases_filters_cleared', {
            previous_search: searchTerm,
            previous_sector: selectedSector,
            previous_type: selectedType
        });
        setSearchTerm('');
        setSelectedSector('All');
        setSelectedType('All');
    };

    // Track search with debounce
    useEffect(() => {
        if (searchTerm.length > 2) {
            const timer = setTimeout(() => {
                trackEvent('usecases_search', {
                    search_term: searchTerm,
                    results_count: filteredUseCases.length
                });
            }, 500);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, filteredUseCases.length]);

    // Track filter changes
    const handleSectorChange = (sector) => {
        setSelectedSector(sector);
        if (sector !== 'All') {
            trackEvent('usecases_filter_sector', {
                sector: sector,
                results_count: remainingUseCases.filter(uc => uc.category === sector).length
            });
        }
    };

    const handleTypeChange = (type) => {
        setSelectedType(type);
        if (type !== 'All') {
            trackEvent('usecases_filter_type', {
                type: type,
                results_count: remainingUseCases.filter(uc => uc.type === type).length
            });
        }
    };

    // Track view mode change
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        trackEvent('usecases_view_mode_changed', { mode: mode });
    };

    // Track use case click
    const handleUseCaseClick = (useCase, location) => {
        trackEvent('usecase_clicked', {
            day: useCase.day,
            title: useCase.title,
            category: useCase.category,
            type: useCase.type,
            location: location, // 'featured' or 'list' or 'grid'
            link: useCase.link
        });
    };

    const hasActiveFilters = searchTerm !== '' || selectedSector !== 'All' || selectedType !== 'All';

    return (
        <>
            <SEO
                title="AI Digital Twin Use Cases | Nebulyx AI"
                description="Explore real-world AI Digital Twin case studies across Retail, Healthcare, Manufacturing, Transport and Finance. See how businesses solve complex problems with data-driven insights."
                keywords="AI digital twin, use cases, case studies, retail analytics, healthcare optimisation, supply chain, route optimisation"
                canonicalUrl="https://nebulyx.ai/use-cases"
                ogType="website"
            />

            <div className="home-page use-cases-page">
                {/* Sticky Search & Filter Bar */}
                <div className="sticky-filter-wrapper">
                    <Container>
                        <div className="sticky-filter-bar">
                            <Row className="g-2 align-items-center">
                                <Col lg={5} md={12}>
                                    <InputGroup>
                                        <InputGroup.Text className="filter-icon">
                                            <FontAwesomeIcon icon={faSearch} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search use cases..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="filter-input"
                                        />
                                    </InputGroup>
                                </Col>
                                <Col lg={2} md={4}>
                                    <InputGroup>
                                        <InputGroup.Text className="filter-icon">
                                            <FontAwesomeIcon icon={faIndustry} />
                                        </InputGroup.Text>
                                        <Form.Select
                                            value={selectedSector}
                                            onChange={(e) => handleSectorChange(e.target.value)}
                                            className="filter-input"
                                        >
                                            {sectors.map(sector => (
                                                <option key={sector} value={sector}>
                                                    {sector === 'All' ? 'All Sectors' : sector}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </InputGroup>
                                </Col>
                                <Col lg={2} md={4}>
                                    <InputGroup>
                                        <InputGroup.Text className="filter-icon">
                                            <FontAwesomeIcon icon={faChartLine} />
                                        </InputGroup.Text>
                                        <Form.Select
                                            value={selectedType}
                                            onChange={(e) => handleTypeChange(e.target.value)}
                                            className="filter-input"
                                        >
                                            {types.map(type => (
                                                <option key={type} value={type}>
                                                    {type === 'All' ? 'All Types' : type}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </InputGroup>
                                </Col>
                                <Col lg={2} md={3} className="d-flex align-items-center gap-2">
                                    <div className="view-toggle-group">
                                        <Button
                                            variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => handleViewModeChange('list')}
                                            className="view-btn"
                                        >
                                            <FontAwesomeIcon icon={faList} />
                                        </Button>
                                        <Button
                                            variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => handleViewModeChange('grid')}
                                            className="view-btn"
                                        >
                                            <FontAwesomeIcon icon={faThLarge} />
                                        </Button>
                                    </div>
                                    {hasActiveFilters && (
                                        <Button
                                            variant="link"
                                            className="clear-btn"
                                            onClick={clearFilters}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </Button>
                                    )}
                                </Col>
                                <Col lg={1} md={1} className="d-none d-lg-block text-end">
                                    <span className="results-badge">
                                        {publishedUseCases.length}
                                    </span>
                                </Col>
                            </Row>

                            {hasActiveFilters && (
                                <div className="active-filters-row mt-2">
                                    {searchTerm && (
                                        <Badge className="filter-pill" bg="secondary">
                                            "{searchTerm}"
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                className="ms-2 remove-filter"
                                                onClick={() => setSearchTerm('')}
                                            />
                                        </Badge>
                                    )}
                                    {selectedSector !== 'All' && (
                                        <Badge
                                            className="filter-pill"
                                            style={{
                                                backgroundColor: 'white',
                                                color: getCategoryStyle(selectedSector).text,
                                                border: `2px solid ${getCategoryStyle(selectedSector).border}`
                                            }}
                                        >
                                            {selectedSector}
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                className="ms-2 remove-filter"
                                                onClick={() => setSelectedSector('All')}
                                            />
                                        </Badge>
                                    )}
                                    {selectedType !== 'All' && (
                                        <Badge
                                            className="filter-pill"
                                            style={{
                                                backgroundColor: 'white',
                                                color: getTypeStyle(selectedType).text,
                                                border: `2px solid ${getTypeStyle(selectedType).border}`
                                            }}
                                        >
                                            {selectedType}
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                className="ms-2 remove-filter"
                                                onClick={() => setSelectedType('All')}
                                            />
                                        </Badge>
                                    )}
                                </div>
                            )}
                        </div>
                    </Container>
                </div>

                {/* Scrollable Content */}
                <div className="scrollable-content">
                    {/* Featured Use Case - Current Day */}
                    {featuredUseCase && (
                        <section className="featured-usecase-section">
                            <Container>
                                <Row className="align-items-center">
                                    <Col lg={7} className="ps-lg-5">
                                        <Badge className="latest-badge mb-3">
                                            <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                                            {featuredUseCase.day} - Latest Use Case
                                        </Badge>
                                        <div className="featured-badges mb-3">
                                            <Badge
                                                className="sector-pill me-2"
                                                style={{
                                                    backgroundColor: 'white',
                                                    color: getCategoryStyle(featuredUseCase.category).text,
                                                    border: `2px solid ${getCategoryStyle(featuredUseCase.category).border}`
                                                }}
                                            >
                                                <FontAwesomeIcon icon={featuredUseCase.icon} className="me-2" />
                                                {featuredUseCase.category}
                                            </Badge>
                                            <Badge
                                                className="type-pill"
                                                style={{
                                                    backgroundColor: 'white',
                                                    color: getTypeStyle(featuredUseCase.type).text,
                                                    border: `2px solid ${getTypeStyle(featuredUseCase.type).border}`
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                                {featuredUseCase.type}
                                            </Badge>
                                        </div>
                                        <h1>{featuredUseCase.title}</h1>
                                        <p className="lead-text">{featuredUseCase.summary}</p>
                                        <p className="description-text">{featuredUseCase.content}</p>
                                        <Link
                                            to={featuredUseCase.link}
                                            onClick={() => handleUseCaseClick(featuredUseCase, 'featured')}
                                        >
                                            <Button className="cta-button mt-3">
                                                Read Full Case Study
                                                <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                                            </Button>
                                        </Link>
                                    </Col>
                                    <Col lg={5} className="d-none d-lg-flex justify-content-center">
                                        <div className="featured-icon-circle">
                                            <FontAwesomeIcon icon={featuredUseCase.icon} />
                                        </div>
                                    </Col>
                                </Row>
                            </Container>
                        </section>
                    )}

                    {/* Use Cases List */}
                    {remainingUseCases.length > 0 && (
                        <section className="usecases-list-section">
                            <Container>
                                <h2 className="mb-4">Previous Use Cases</h2>
                                {filteredUseCases.length === 0 ? (
                                    <Card className="no-results-card text-center p-5">
                                        <div className="no-results-icon mb-3">
                                            <FontAwesomeIcon icon={faSearch} />
                                        </div>
                                        <h3>No use cases found</h3>
                                        <p className="text-muted">Try adjusting your search or filters</p>
                                        <Button variant="outline-primary" onClick={clearFilters} className="mt-2">
                                            Clear All Filters
                                        </Button>
                                    </Card>
                                ) : viewMode === 'list' ? (
                                    <Row className="g-4">
                                        {filteredUseCases.map((useCase) => (
                                            <Col xs={12} key={useCase.day}>
                                                <Card className="usecase-card">
                                                    <Card.Body>
                                                        <Row className="align-items-center">
                                                            <Col lg={1} md={2} className="d-none d-md-flex justify-content-center">
                                                                <div
                                                                    className="usecase-icon-circle"
                                                                    style={{ backgroundColor: getCategoryStyle(useCase.category).bg }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={useCase.icon}
                                                                        style={{ color: getCategoryStyle(useCase.category).text }}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col lg={8} md={7}>
                                                                <div className="usecase-badges mb-2">
                                                                    <Badge className="day-pill me-2">
                                                                        {useCase.day}
                                                                    </Badge>
                                                                    <Badge
                                                                        className="sector-pill-sm me-2"
                                                                        style={{
                                                                            backgroundColor: 'white',
                                                                            color: getCategoryStyle(useCase.category).text,
                                                                            border: `2px solid ${getCategoryStyle(useCase.category).border}`
                                                                        }}
                                                                    >
                                                                        {useCase.category}
                                                                    </Badge>
                                                                    <Badge
                                                                        className="type-pill-sm"
                                                                        style={{
                                                                            backgroundColor: 'white',
                                                                            color: getTypeStyle(useCase.type).text,
                                                                            border: `2px solid ${getTypeStyle(useCase.type).border}`
                                                                        }}
                                                                    >
                                                                        {useCase.type}
                                                                    </Badge>
                                                                </div>
                                                                <h3 className="usecase-title">
                                                                    <Link
                                                                        to={useCase.link}
                                                                        onClick={() => handleUseCaseClick(useCase, 'list')}
                                                                    >
                                                                        {useCase.title}
                                                                    </Link>
                                                                </h3>
                                                                <p className="usecase-summary">{useCase.summary}</p>
                                                                <p className="usecase-excerpt">{useCase.content}</p>
                                                            </Col>
                                                            <Col lg={3} md={3} className="text-md-end mt-3 mt-md-0">
                                                                <Link
                                                                    to={useCase.link}
                                                                    onClick={() => handleUseCaseClick(useCase, 'list')}
                                                                >
                                                                    <Button className="cta-button">
                                                                        Read Case Study
                                                                        <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                                                                    </Button>
                                                                </Link>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    <Row className="g-4">
                                        {filteredUseCases.map((useCase) => (
                                            <Col lg={4} md={6} key={useCase.day}>
                                                <Card className="usecase-grid-card h-100">
                                                    <Card.Body className="d-flex flex-column">
                                                        <div className="grid-header mb-3">
                                                            <div
                                                                className="usecase-icon-circle"
                                                                style={{ backgroundColor: getCategoryStyle(useCase.category).bg }}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={useCase.icon}
                                                                    style={{ color: getCategoryStyle(useCase.category).text }}
                                                                />
                                                            </div>
                                                            <div className="grid-badges">
                                                                <Badge className="day-pill me-1">
                                                                    Day {useCase.day}
                                                                </Badge>
                                                                <Badge
                                                                    className="sector-pill-sm"
                                                                    style={{
                                                                        backgroundColor: 'white',
                                                                        color: getCategoryStyle(useCase.category).text,
                                                                        border: `2px solid ${getCategoryStyle(useCase.category).border}`
                                                                    }}
                                                                >
                                                                    {useCase.category}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        <h3 className="usecase-title">
                                                            <Link
                                                                to={useCase.link}
                                                                onClick={() => handleUseCaseClick(useCase, 'grid')}
                                                            >
                                                                {useCase.title}
                                                            </Link>
                                                        </h3>
                                                        <p className="usecase-summary">{useCase.summary}</p>
                                                        <div className="mt-auto pt-3">
                                                            <Link
                                                                to={useCase.link}
                                                                onClick={() => handleUseCaseClick(useCase, 'grid')}
                                                            >
                                                                <Button className="cta-button w-100">
                                                                    Read Case Study
                                                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                                                                </Button>
                                                            </Link>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                )}

                                {filteredUseCases.length > 0 && (
                                    <p className="results-count text-center text-muted mt-4">
                                        Showing {filteredUseCases.length} previous use case{filteredUseCases.length !== 1 ? 's' : ''}
                                        {selectedSector !== 'All' && ` in ${selectedSector}`}
                                        {selectedType !== 'All' && ` • ${selectedType}`}
                                    </p>
                                )}
                            </Container>
                        </section>
                    )}

                    {/* CTA Section */}
                    <section className="final-cta-section">
                        <Container>
                            <h2>Have a Business Challenge?</h2>
                            <p className="cta-description">
                                Let's explore how an AI Digital Twin can diagnose your problem
                                and uncover insights hidden from traditional analytics.
                            </p>
                            <Button
                                className="cta-button-light"
                                size="lg"
                                href="https://cal.eu/jon-cooke/discovery-call"
                                onClick={() => trackBookingClick('usecases-page-cta')}
                            >
                                Book a Discovery Call
                                <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                            </Button>
                        </Container>
                    </section>
                </div>
            </div>
        </>
    );
};

export default UseCases;